import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { shortDate, money } from "@/lib/api";
import { Plus, Camera, FileEdit, Filter } from "lucide-react";

export default function StoreVisits() {
  const [visits, setVisits] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const [showDraftsOnly, setShowDraftsOnly] = useState(false);

  useEffect(() => { api.get("/agents").then(r => setAgents(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/visits", { params: agent ? { agent_id: agent } : {} }).then(r => setVisits(r.data));
  }, [agent]);

  const shown = showDraftsOnly ? visits.filter(v => v.draft) : visits;
  const draftCount = visits.filter(v => v.draft).length;

  return (
    <div className="space-y-6" data-testid="visits-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-black tracking-tight">Store Visits</h1>
          <p className="text-slate-600 mt-1">
            {visits.length} recorded{draftCount > 0 && <> • <span className="text-[#003DA5] font-semibold">{draftCount} draft{draftCount > 1 ? "s" : ""}</span></>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowDraftsOnly(!showDraftsOnly)}
            className={`h-11 px-4 rounded-lg font-semibold text-sm flex items-center gap-2 transition ${showDraftsOnly ? "bg-[#001F54] text-white" : "border border-slate-300"}`}
            data-testid="drafts-toggle"
          >
            <Filter className="w-4 h-4" /> {showDraftsOnly ? "All Visits" : `Drafts (${draftCount})`}
          </button>
          {agents.length > 0 && (
            <select value={agent} onChange={(e) => setAgent(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="visits-agent-filter">
              <option value="">All Agents</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <Link to="/visits/new" className="mlily-btn h-11 px-5 rounded-lg font-semibold flex items-center gap-2" data-testid="new-visit-btn">
            <Plus className="w-4 h-4" /> New Visit
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
        {shown.map(v => (
          <Link
            key={v.id}
            to={v.draft ? `/visits/${v.id}/edit` : `/visits/${v.id}/edit`}
            className={`block bg-white rounded-xl border p-5 card-hoverable ${v.draft ? "border-[#003DA5]/40 bg-red-50/20" : "border-slate-200"}`}
            data-testid={`visit-card-${v.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{shortDate(v.visit_date)}</div>
                  {v.draft && <span className="text-[10px] uppercase tracking-widest font-bold text-[#003DA5] flex items-center gap-1"><FileEdit className="w-3 h-3" /> Draft</span>}
                </div>
                <div className="text-lg font-semibold mt-1">{v.customer_name}</div>
                <div className="text-xs text-slate-500">{v.town}, {v.state}</div>
              </div>
              {v.photo_ids?.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-slate-500"><Camera className="w-3 h-3" />{v.photo_ids.length}</div>
              )}
            </div>
            <div className="text-sm text-slate-600 mt-3 line-clamp-2">{v.visit_notes || v.visit_purpose}</div>
            <div className="flex justify-between items-center mt-4 text-xs">
              <span className="text-slate-500">{v.agent_name}</span>
              {v.estimated_opportunity_value > 0 && <span className="font-semibold text-[#003DA5]">{money(v.estimated_opportunity_value)} opp.</span>}
            </div>
          </Link>
        ))}
        {shown.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500">
            {showDraftsOnly ? "No drafts — everything submitted." : "No visits recorded yet."}
          </div>
        )}
      </div>
    </div>
  );
}
